import { FormView } from '../../UI/view/form-view';
import { FormItemType } from '../../UI/view/component-map';
import { initView } from '../../UI/view/view';
import { DataSource } from '../../UI/view/data-source';
import { getBookingLookups, getConsigneeDefaults, insertBooking } from '../../../services/requests/bookings/bookings-requests';
import { bookingsPermissions } from './bookings-datasource';
import { BookingOrderPrefill } from './bookings-order-prefill';
import { applyConsigneeDefaults } from './bookings-consignee';

const OTHER = 'Other...';
const OTHER_PORT = -1;

// Legacy RegularExpressionValidator: the first match must span the whole value.
const fullMatch = (value: string, pattern: RegExp) => {
  const m = pattern.exec(value);
  return !!m && m.index === 0 && m[0].length === value.length;
};

const customerOptions = async () => {
  const { customers } = await getBookingLookups();
  return [{ id: 0, text: '-- Select --' }, ...customers.map((c) => ({ id: c.customerId, text: c.customerName }))];
};
const containerOptions = async () => {
  const { containers } = await getBookingLookups();
  return [{ id: '', text: '-- Select --' }, ...containers.map((c) => ({ id: c, text: c }))];
};
const agentOptions = async () => {
  const { bookingAgents } = await getBookingLookups();
  return [{ id: '', text: '--Please Select--' }, ...bookingAgents.map((a) => ({ id: a, text: a }))];
};
const portOptions = async () => {
  const { ports } = await getBookingLookups();
  return [
    { id: 0, text: '-- Select --' },
    { id: OTHER_PORT, text: OTHER },
    ...ports.map((p) => ({ id: p.terminalLocationId, text: p.terminalName })),
  ];
};

const portFields = (name: string, otherName: string, label: string) => [
  {
    name,
    label,
    type: FormItemType.select,
    searchable: true,
    required: true,
    defaultValue: 0,
    zeroIsEmpty: true,
    options: portOptions,
    onChange: ({ setValue }: any) => setValue(otherName, ''),
  },
  {
    name: otherName,
    label: `${label} (Other)`,
    maxLength: 100,
    hideIf: ({ watch }: any) => Number(watch?.(name)) !== OTHER_PORT,
    required: true,
  },
];

export const bookingsAdd = {
  pageHeader: 'Logistics - Bookings - New',
  editMode: false,
  disableDirtyCheck: true,
  permissions: bookingsPermissions,
  breadcrumbs: [{ name: 'Bookings', icon: 'calendar-check', to: '/bookings' }],
  // Legacy dvBooking_ItemInserted redirects back to Bookings.aspx.
  redirectOnCreate: () => '/bookings',
  items: [
    { type: FormItemType.customComponent, component: BookingOrderPrefill, name: 'orderPrefill' },
    {
      type: FormItemType.fieldset,
      label: "Fields marked with '*' are required.",
      className: 'compact-fields',
      items: [
        {
          type: FormItemType.row,
          items: [
            {
              name: 'customerId',
              label: 'Customer ID',
              type: FormItemType.select,
              searchable: true,
              required: true,
              defaultValue: 0,
              zeroIsEmpty: true,
              options: customerOptions,
              onChange: async ({ setValue, evt }: any) => {
                const customerId = parseInt(evt?.target?.value, 10);
                if (Number.isNaN(customerId)) return;
                applyConsigneeDefaults(await getConsigneeDefaults(customerId), setValue);
              },
            },
            { name: 'containerId', label: 'Container ID', type: FormItemType.select, searchable: true, defaultValue: '', options: containerOptions },
            {
              name: 'fullEmptyType',
              label: 'Full/Empty',
              type: FormItemType.select,
              defaultValue: '',
              options: () => [{ id: '', text: '-- Select --' }, { id: 'Full', text: 'Full' }, { id: 'Empty', text: 'Empty' }],
            },
            {
              name: 'containerSize',
              label: 'Cont/Module Size',
              type: FormItemType.select,
              defaultValue: '',
              options: () => [{ id: '', text: '-- Select --' }, { id: '20ft', text: '20ft' }, { id: '40ft', text: '40ft' }],
            },
          ],
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'plantId',
              label: 'Sourcing Plant',
              type: FormItemType.select,
              required: true,
              defaultValue: '',
              options: () => [
                { id: '', text: '-- Select --' },
                { id: 'Alg', text: 'Algeria' },
                { id: 'Qat', text: 'Qatar' },
                { id: 'USA', text: 'USA' },
              ],
            },
            { name: 'poNumber', label: 'PO Number', required: true, defaultValue: '' },
            { name: 'lindeOrderId', label: 'Order ID (new)', defaultValue: '' },
            { name: 'releaseNumber', label: 'Release Number', defaultValue: '' },
          ],
        },
        {
          type: FormItemType.row,
          items: [
            { name: 'dateRequested', label: 'Date Requested', type: FormItemType.date, required: true },
            { name: 'earliestDeliveryDate', label: 'Earliest Delivery Date', type: FormItemType.date },
            { name: 'dateOrderReceived', label: 'Date Order Received', type: FormItemType.date },
          ],
        },
        { name: 'remarks', label: 'Linde Remarks', type: FormItemType.textarea, defaultValue: '' },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'bookingAgentSelect',
              label: 'Booking Agent',
              type: FormItemType.select,
              searchable: true,
              required: true,
              defaultValue: '',
              options: agentOptions,
              onChange: ({ setValue }: any) => setValue('bookingAgentOther', ''),
            },
            {
              name: 'bookingAgentOther',
              label: 'Booking Agent (Other)',
              maxLength: 100,
              required: true,
              hideIf: ({ watch }: any) => watch?.('bookingAgentSelect') !== OTHER,
              validation: {
                email: {
                  message: 'The email is not well formed.',
                  handler: (v: string) => !v || fullMatch(v, /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/),
                },
              },
            },
          ],
        },
        {
          type: FormItemType.row,
          items: [
            ...portFields('usPort', 'usPortOther', 'Supply Plant Port'),
            ...portFields('customerPort', 'customerPortOther', 'Customer Port'),
          ],
        },
        {
          type: FormItemType.row,
          items: [
            { name: 'consigneeName', label: 'Consignee Name', defaultValue: '' },
            { name: 'consigneeNumber', label: 'Consignee Number', defaultValue: '' },
            { name: 'consigneeEmail', label: 'Consignee Email', defaultValue: '' },
            { name: 'consigneePhone', label: 'Consignee Phone', defaultValue: '' },
            { name: 'consigneeFax', label: 'Consignee Fax', defaultValue: '' },
          ],
        },
        {
          type: FormItemType.row,
          items: [
            {
              name: 'orderId',
              label: 'SO Number (Order #)',
              defaultValue: '',
              validation: {
                length: { message: 'Order ID length should be less than 11.', handler: (v: string) => !v || fullMatch(v, /.{0,11}$/) },
              },
            },
            {
              name: 'orderGuid',
              label: 'Order Guid',
              defaultValue: '',
              validation: {
                guid: {
                  message: 'Guid format should be {XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}',
                  handler: (v: string) =>
                    !v || /^(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}$/.test(v),
                },
              },
            },
          ],
        },
        { name: 'notifyAgent', label: 'Notify Booking Agent upon Save', type: FormItemType.checkboxField, defaultValue: true },
      ],
    },
    {
      type: FormItemType.row,
      items: [
        { type: FormItemType.submitButton, label: 'Save', permissions: bookingsPermissions },
        { type: FormItemType.button, label: 'Cancel', handler: ({ redirect }: any) => redirect('/bookings') },
      ],
    },
  ],
} as FormView;

const bookingsAddDS = {
  name: 'bookingsAdd',
  request: getBookingLookups,
  createRequest: insertBooking,
  pk: 'bookingId',
} as DataSource;

export const BookingsAddFormView = initView({ form: bookingsAdd, dataSource: bookingsAddDS });
